import { ContactInfo, MarketoForm, useAuth, useContact } from '@/context';
import React from 'react';

import { MarketoFormProps } from '../MarketoForm.interface';

interface CRMSync {
  CRMSyncFieldnames?: MarketoFormProps['CRMSyncFieldnames'];
  emailFieldname?: string;
  emailCRMFieldname?: string;
}

export const useCRMSync = ({ CRMSyncFieldnames, emailFieldname, emailCRMFieldname }: CRMSync) => {
  const { isAuthorized } = useAuth();
  const { contactInfo, updateContactInfo } = useContact();
  const [CRMSyncValues, setCRMSyncValues] = React.useState<Record<string, string>>({});

  const syncCRMValues = React.useCallback(
    (form: MarketoForm, values?: Record<string, string>) => {
      if (!form) return;
      if (!isAuthorized) return;
      if (!CRMSyncFieldnames?.length) return;

      const { crmSync, marketoSync } = CRMSyncFieldnames.reduce(
        (acc, curr) => {
          const value = contactInfo?.[curr.crmName as keyof ContactInfo];

          return {
            crmSync: values || { ...acc.crmSync, [curr.crmName]: value },
            marketoSync: { ...acc.marketoSync, [curr.marketoName]: value },
          };
        },
        { crmSync: {}, marketoSync: {} },
      );

      // Set CRMSyncValues that will only sent after submitting.
      setCRMSyncValues({ ...crmSync });

      // Marketo form population
      if (!emailCRMFieldname) return;
      if (!contactInfo?.[emailCRMFieldname]) return;
      const email = contactInfo?.[emailCRMFieldname];

      const marketoValues = form?.getValues();
      const hasVisibleEmail = Object.keys(marketoValues).find(
        (key = '') => emailFieldname?.toLowerCase() === key?.toLowerCase(),
      );

      if (emailFieldname && hasVisibleEmail) {
        form.setDefaultValues({
          ...marketoValues,
          ...marketoSync,
          [emailFieldname]: email,
        });
        return;
      }

      if (emailFieldname) {
        form?.addHiddenFields({ ...marketoValues, [emailFieldname]: email });
      }

      form?.setDefaultValues({ ...marketoValues, ...marketoSync });
    },
    [isAuthorized, CRMSyncFieldnames, emailCRMFieldname, emailFieldname, contactInfo],
  );

  const submitCRMSyncValues = React.useCallback(
    async (form: MarketoForm, values?: Record<string, string>): Promise<any> => {
      // Only for authorized users
      if (!isAuthorized) return Promise.resolve();

      const nextCRMSyncValues = values
        ? CRMSyncFieldnames?.reduce((prev, curr) => {
            const value = values?.[curr.marketoName];

            return { ...prev, [curr.crmName]: value };
          }, CRMSyncValues)
        : CRMSyncValues;

      if (!updateContactInfo || !nextCRMSyncValues || !Object.keys(nextCRMSyncValues).length)
        return Promise.reject();

      try {
        await updateContactInfo(nextCRMSyncValues, true);
      } catch (e) {
        return Promise.reject(e);
      }

      setTimeout(() => syncCRMValues(form, nextCRMSyncValues));

      return Promise.resolve();
    },
    [isAuthorized, CRMSyncFieldnames, CRMSyncValues, syncCRMValues, updateContactInfo],
  );

  return { syncCRMValues, submitCRMSyncValues };
};
